import { useEffect, useMemo, useState } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, Download } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { WhatsAppIcon } from "@/components/WhatsAppBadge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = process.env.REACT_APP_BACKEND_URL;

export const CATEGORY_DESCRIPTIONS = {
  spezie: "Spezie e aromi selezionati per la cucina mediterranea ed etnica: cumino, paprika, ras el hanout, curry e preparati per merguez, in formati per il banco e per la ristorazione.",
  bibite: "Bibite e succhi per negozi etnici e ristoranti: lattine, PET e vetro dei marchi più richiesti dai tuoi clienti.",
  olive: "Olive e conserve in formati per banco e Ho.Re.Ca: verdi, nere, condite, sottaceti e cocktail.",
  salumi: "Salumi e affettati halal di pollo, tacchino e manzo: galantine, mortadelle e specialità per la gastronomia.",
  tisane: "Tè e infusi pregiati: tè verde, tè nero e tisane della tradizione per il rituale del tè.",
  olio: "Olio di oliva ed extravergine in bottiglie e lattine, per retail e ristorazione.",
  "frutta-secca": "Frutta secca ed essiccata: mandorle, pistacchi, noci, uvetta, albicocche e fichi secchi.",
  couscous: "Couscous e semole di grano duro fine, medio, completo e belboula, più pasta, vermicelli e capelli d'angelo.",
  salse: "Salse per kebab, tacos e street food: algerina, samourai, harissa, maionese, anche in formato da 5 litri.",
  freschi: "Prodotti freschi mediterranei: formaggi, latte fermentato, foglie di brick e specialità refrigerate.",
  farina: "Farine, semole e lieviti per panificazione e pasticceria, in sacchi da 1 e 5 kg.",
  dadi: "Dadi da brodo e preparati per cucina ai gusti della tradizione mediterranea.",
  tonno: "Tonno e conserve ittiche all'olio o in salsa, in lattine per retail e ristorazione.",
  legumi: "Legumi secchi e decorticati: ceci, lenticchie rosse e mignon, fave e fagioli cannellini.",
  bazar: "Articoli bazar e casalinghi: teiere, bicchieri per il tè, tajine e piccoli elettrodomestici.",
  datteri: "Datteri premium: Medjoul, Deglet Nour, Ajwa e varietà da Tunisia, Algeria, Arabia Saudita e Marocco.",
  miele: "Miele millefiori, di bosco e di montagna, più confetture di frutta e crema di nocciole.",
  dolci: "Dolci e dessert della tradizione: baklava, maamoul e specialità da forno orientali.",
  surgelati: "Surgelati mediterranei: verdure, falafel, pasta fillo, molokhia e preparati pronti.",
  tahina: "Tahina e halawa: crema di sesamo e dolce di sesamo al naturale, al pistacchio o alle mandorle.",
  altri: "Il meglio del resto dell'assortimento: riso basmati, latte in polvere, conserve di pomodoro e specialità.",
};

export default function CategoryPage() {
  const { slug } = useParams();
  const [products, setProducts] = useState(null);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    axios.get(`${API}/products`)
      .then((r) => { setProducts(r.data.products); setCategories(r.data.categories); })
      .catch(() => toast.error("Errore di caricamento", { description: "Riprova tra poco." }));
  }, []);

  const category = categories.find((c) => c.slug === slug);
  const items = useMemo(() => (products || []).filter((p) => p.category === slug), [products, slug]);

  if (products && categories.length && !category) return <Navigate to="/prodotti" replace />;

  const label = category?.label || "";
  const desc = CATEGORY_DESCRIPTIONS[slug] || "";
  const waText = encodeURIComponent(`Salve Dahmani Food, vorrei il listino per la categoria ${label}.`);

  return (
    <div data-testid="category-page" className="bg-[#faf8f5] text-[#0a192f] min-h-screen">
      <Seo
        title={`${label} all'Ingrosso | Dahmani Food Halal`}
        description={`${desc} Distribuzione all'ingrosso Dahmani Food — consegne in tutta Italia.`}
        path={`/prodotti/${slug}`}
      />
      <Navbar />

      <header className="relative bg-[#0b2545] text-white overflow-hidden">
        <div className="absolute inset-0 df-pattern-gold" />
        <div className="absolute -right-20 -bottom-20 opacity-[0.12] pointer-events-none">
          <img src="/images/logo.png" alt="" aria-hidden="true" className="w-[380px] h-[380px] rounded-full" />
        </div>
        <div className="relative max-w-6xl mx-auto px-6 py-14 md:py-16 space-y-4">
          <Link to="/prodotti" data-testid="category-back" className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-white/60 hover:text-[#d4af37] transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" /> Tutto il catalogo
          </Link>
          <h1 className="font-display text-4xl sm:text-5xl font-normal tracking-tight">
            {label} <span className="italic text-[#d4af37]">all'ingrosso</span>
          </h1>
          <p className="text-sm text-white/70 max-w-2xl leading-relaxed">{desc}</p>
          <p className="text-xs font-mono-df text-[#d4af37]">{items.length} referenze · Consegne in tutta Italia</p>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12">
        {!products ? (
          <p className="text-center text-sm text-[#0b2545]/60 py-24">Caricamento…</p>
        ) : items.length === 0 ? (
          <p className="text-center text-sm text-[#0b2545]/60 py-24">Le referenze di questa categoria saranno presto online.</p>
        ) : (
          <div data-testid="category-products-grid" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
            {items.map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-20px" }}
                transition={{ duration: 0.4, delay: (i % 5) * 0.05 }}
                data-testid={`product-card-${p.id}`}
                className="group rounded-2xl bg-white border border-[#0b2545]/10 overflow-hidden shadow-sm hover:shadow-lg hover:border-[#d4af37]/50 transition-all duration-300"
              >
                <div className="aspect-square bg-white overflow-hidden">
                  {p.image_url ? (
                    <img src={`${BASE}${p.image_url}`} alt={`${p.name} — ${label} all'ingrosso Dahmani Food`} loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-[#f5efe4]">
                      <img src="/images/logo.png" alt="" className="w-16 h-16 rounded-2xl opacity-60" />
                      <span className="text-[9px] uppercase font-mono-df tracking-widest text-[#0b2545]/40">Foto in arrivo</span>
                    </div>
                  )}
                </div>
                <div className="p-3.5 space-y-1">
                  <p className="text-xs font-bold text-[#0b2545] leading-snug">{p.name}</p>
                  <p className="text-[10px] font-mono-df text-[#c59b27]">
                    {[p.format, p.box_qty ? `${p.box_qty} pz/box` : null].filter(Boolean).join(" · ")}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        <div className="mt-14 p-8 md:p-10 rounded-3xl bg-gradient-to-r from-[#0b2545] to-[#06152d] text-white flex flex-col md:flex-row items-center justify-between gap-6 border border-[#d4af37]/30">
          <div className="space-y-2 text-center md:text-left">
            <h2 className="font-display text-2xl font-medium">Vuoi il listino {label.toLowerCase()}?</h2>
            <p className="text-xs text-white/70 max-w-md">Scrivici su WhatsApp o scarica il catalogo completo in PDF.</p>
          </div>
          <div className="flex flex-wrap gap-3 justify-center">
            <a data-testid="category-whatsapp-cta" href={`https://wa.me/393756088043?text=${waText}`} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#25D366] text-white text-xs font-bold uppercase tracking-wider hover:bg-white hover:text-[#06152d] transition-colors">
              <WhatsAppIcon className="w-4 h-4" /> WhatsApp
            </a>
            <a data-testid="category-pdf-cta" href={`${API}/catalog.pdf?v=${Date.now()}`} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#d4af37] text-[#06152d] text-xs font-bold uppercase tracking-wider hover:bg-white transition-colors">
              <Download className="w-4 h-4" /> Catalogo PDF
            </a>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
